import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import '../Components/AuditOperationDetail.css';
import '../Components/AgentAuditOperationDetail.css';
import Breadcrumbs from '../Components/Breadcrumbs';
import { ConfirmationProvider } from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import {
  ConnectionLogApiProvider,
  useConnectionLogApi,
} from '../Services/ConnectionLogApiProvider';
import { formatDateUTC } from '../utils/formatDate';

function parseNestedJson(value) {
  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    const looksLikeJson =
      (trimmedValue.startsWith('{') && trimmedValue.endsWith('}')) ||
      (trimmedValue.startsWith('[') && trimmedValue.endsWith(']'));

    if (!looksLikeJson) return value;

    try {
      return parseNestedJson(JSON.parse(trimmedValue));
    } catch {
      return value;
    }
  }

  if (Array.isArray(value)) {
    return value.map(parseNestedJson);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([key, nestedValue]) => [key, parseNestedJson(nestedValue)]),
    );
  }

  return value;
}

function JsonBlock({ title, value, emptyMessage }) {
  return (
    <div className="radius-yes p-2 bg-yes col-12">
      <h2 className="h5-title">{title}</h2>
      {value === null || value === undefined ? (
        <p>{emptyMessage}</p>
      ) : (
        <pre className="tool-pre">{JSON.stringify(parseNestedJson(value), null, 2)}</pre>
      )}
    </div>
  );
}

function ConnectionRequestDetail() {
  const { connectionRequestId } = useParams();
  const { getConnectionLogById } = useConnectionLogApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [connectionLog, setConnectionLog] = useState(null);

  useEffect(() => {
    const fetchConnectionLog = async () => {
      setLoading(true);
      try {
        const response = await getConnectionLogById(connectionRequestId);
        if (response) {
          setConnectionLog(response);
        }
      } catch (error) {
        console.error('Error fetching connection log:', error);
        setError('Failed to load connection request');
      } finally {
        setLoading(false);
      }
    };

    fetchConnectionLog();
  }, [connectionRequestId, getConnectionLogById]);

  const properties = connectionLog?.properties;

  return (
    <div className="container">
      <Breadcrumbs />

      {loading ? (
        <PageLoader />
      ) : error ? (
        <div className="radius-yes p-2 bg-yes col-12">Error: {error}</div>
      ) : (
        <div className="row d-flex gap">
          <div className="radius-yes p-2 bg-yes col-12">
            <h2 className="h5-title">Connection Request</h2>
            <div className="radius-yes bg-yes">
              <div className="detail-item">
                <label>
                  <strong>Request Id:</strong>
                </label>
                <span>{connectionLog?.id || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Connection:</strong>
                </label>
                <span>{connectionLog?.connection_name || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Connection Key:</strong>
                </label>
                <span>{connectionLog?.connection_key || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Connection Type:</strong>
                </label>
                <span>{connectionLog?.connection_type || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Operation:</strong>
                </label>
                <span>{connectionLog?.operation || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Status:</strong>
                </label>
                <span>{connectionLog?.status || '-'}</span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Created At:</strong>
                </label>
                <span>
                  {connectionLog?.created_at ? formatDateUTC(connectionLog.created_at) : '-'}
                </span>
              </div>
              <div className="detail-item">
                <label>
                  <strong>Duration:</strong>
                </label>
                <span>
                  {connectionLog?.duration != null
                    ? `${Number(connectionLog.duration).toFixed(2)} s`
                    : '-'}
                </span>
              </div>
            </div>
          </div>

          <JsonBlock
            title="Request"
            value={properties?.request}
            emptyMessage="No request data captured."
          />
          <JsonBlock
            title="Response"
            value={properties?.response}
            emptyMessage="No response data captured."
          />
          {properties?.error && (
            <JsonBlock title="Error" value={properties.error} emptyMessage="No error captured." />
          )}
        </div>
      )}
    </div>
  );
}

function ConnectionRequestDetailWrapper() {
  return (
    <ConfirmationProvider>
      <ConnectionLogApiProvider>
        <ConnectionRequestDetail />
      </ConnectionLogApiProvider>
    </ConfirmationProvider>
  );
}

export default ConnectionRequestDetailWrapper;
